import { useLazyQuery, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  CREATE_CUSTOMER_MUTATION,
  LOOKUP_CUSTOMER_DUPLICATES_QUERY,
  UPDATE_CUSTOMER_MUTATION,
} from './customers.gql';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';

const schema = z.object({
  first_name: z.string().trim().min(1).max(120),
  last_name: z.string().trim().max(120).optional().or(z.literal('')),
  phone: z.string().trim().min(7).max(40),
  email: z.string().trim().email().max(160).optional().or(z.literal('')),
  rfc: z.string().trim().max(13).optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

interface CustomerFormDialogProps {
  mode: 'create' | 'edit';
  initial_values?: Partial<FormValues> & { id?: string };
  onClose: () => void;
  onSaved: (id: string) => void | Promise<void>;
}

interface DuplicateMatch {
  customer: {
    id: string;
    first_name: string;
    last_name: string | null;
    phone: string;
    email: string | null;
  };
  match_score: number;
  match_reason: string;
}

interface CustomerMutationResult {
  id: string;
}

export function CustomerFormDialog({
  mode,
  initial_values,
  onClose,
  onSaved,
}: CustomerFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: initial_values?.first_name ?? '',
      last_name: initial_values?.last_name ?? '',
      phone: initial_values?.phone ?? '',
      email: initial_values?.email ?? '',
      rfc: initial_values?.rfc ?? '',
    },
  });

  const [createCustomer, { loading: creating }] = useMutation<{
    createCustomer: CustomerMutationResult;
  }>(CREATE_CUSTOMER_MUTATION);
  const [updateCustomer, { loading: updating }] = useMutation<{
    updateCustomer: CustomerMutationResult;
  }>(UPDATE_CUSTOMER_MUTATION);
  const [lookupDuplicates, { data: dup_data }] = useLazyQuery<{
    lookupCustomerDuplicates: DuplicateMatch[];
  }>(LOOKUP_CUSTOMER_DUPLICATES_QUERY);

  // Debounced duplicate lookup as the operator types phone/name (create only).
  const watched_phone = form.watch('phone');
  const watched_first = form.watch('first_name');
  const watched_last = form.watch('last_name');
  useEffect(() => {
    if (mode !== 'create') return;
    const phone_digits = watched_phone.replace(/\D/g, '');
    if (phone_digits.length < 7 || watched_first.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      void lookupDuplicates({
        variables: {
          input: {
            first_name: watched_first.trim(),
            last_name: watched_last?.trim() ?? undefined,
            phone: watched_phone,
          },
        },
      });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [mode, watched_phone, watched_first, watched_last, lookupDuplicates]);

  const matches = dup_data?.lookupCustomerDuplicates ?? [];

  const onSubmitCustomerForm = async (values: FormValues): Promise<void> => {
    const last_name = values.last_name?.trim() ?? '';
    const email = values.email?.trim() ?? '';
    const rfc = values.rfc?.trim() ?? '';
    const input = {
      first_name: values.first_name.trim(),
      last_name: last_name.length > 0 ? last_name : null,
      phone: values.phone.trim(),
      email: email.length > 0 ? email : null,
      rfc: rfc.length > 0 ? rfc : null,
    };
    try {
      if (mode === 'create') {
        const result = await createCustomer({ variables: { input } });
        assertMutationSucceeded(result, 'create_customer_failed');
        const id = result.data?.createCustomer?.id;
        if (id) await onSaved(id);
      } else if (mode === 'edit' && initial_values?.id) {
        const result = await updateCustomer({ variables: { id: initial_values.id, input } });
        assertMutationSucceeded(result, 'update_customer_failed');
        await onSaved(initial_values.id);
      }
    } catch (err) {
      showToast({
        tone: 'error',
        title: mode === 'create' ? t('alerts.create_error_title') : t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const submitting = creating || updating;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <form
          onSubmit={(event) => {
            void form.handleSubmit(onSubmitCustomerForm)(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle>
              {mode === 'create' ? t('customers.create') : t('customers.edit')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('customers.first_name')}</Label>
                <Input id="first_name" {...form.register('first_name')} />
                {form.formState.errors.first_name ? (
                  <p className="text-sm text-destructive">{form.formState.errors.first_name.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t('customers.last_name')}</Label>
                <Input id="last_name" {...form.register('last_name')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('customers.phone')}</Label>
              <Input id="phone" inputMode="tel" {...form.register('phone')} />
              {form.formState.errors.phone ? (
                <p className="text-sm text-destructive">{form.formState.errors.phone.message}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="email">{t('customers.email')}</Label>
                <Input id="email" type="email" {...form.register('email')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rfc">{t('customers.rfc')}</Label>
                <Input id="rfc" maxLength={13} className="font-mono uppercase" {...form.register('rfc')} />
              </div>
            </div>

            {mode === 'create' && matches.length > 0 ? (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-300">
                  {t('customers.duplicate_warning')}
                </p>
                <ul className="mt-2 space-y-1 text-amber-700 dark:text-amber-200">
                  {matches.slice(0, 5).map((m) => (
                    <li key={m.customer.id} className="flex items-center justify-between gap-2">
                      <span>
                        {m.customer.first_name}
                        {m.customer.last_name ? ` ${m.customer.last_name}` : ''} —{' '}
                        <span className="font-mono">{m.customer.phone}</span>
                      </span>
                      <Badge variant="outline">
                        {t(`customers.match_${m.match_reason}`, m.match_reason)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
